import { useState, useEffect, useCallback } from 'react'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui'
import { apiClient } from '@/lib/api'
import type { ColumnDef } from '@tanstack/react-table'
import { Search, Plus, Trash2, RefreshCw, ExternalLink } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SeoCheck {
  key: string
  label: string
  status: 'ok' | 'warning' | 'error'
  message: string
  value?: string | number
}

interface SeoAudit {
  id: string
  url: string
  score: number
  title: string | null
  word_count: number
  h1_count: number
  h2_count: number
  internal_link_count: number
  image_count: number
  images_without_alt: number
  ai_recommendations: string | null
  checks: SeoCheck[]
  keywords: string[]
  error_message: string | null
  fetched_at: string | null
  created_at: string
}

interface UrlConfig {
  id: string
  url: string
  label: string | null
  is_active: boolean
  created_at: string
}

interface GscRow {
  keys: string[]
  clicks: number
  impressions: number
  ctr: number
  position: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600'
  if (score >= 50) return 'text-yellow-600'
  return 'text-red-600'
}

function scoreLabel(score: number): string {
  if (score >= 80) return 'Gut'
  if (score >= 50) return 'Mittelmäßig'
  return 'Schlecht'
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso))
}

function checkStatusColor(status: 'ok' | 'warning' | 'error'): string {
  if (status === 'ok') return 'bg-green-100 text-green-800'
  if (status === 'warning') return 'bg-yellow-100 text-yellow-800'
  return 'bg-red-100 text-red-800'
}

function checkStatusText(status: 'ok' | 'warning' | 'error'): string {
  if (status === 'ok') return 'OK'
  if (status === 'warning') return 'Warnung'
  return 'Fehler'
}

// ─── Score Ring ───────────────────────────────────────────────────────────────

function ScoreRing({ score }: { score: number }) {
  const r = 36
  const circumference = 2 * Math.PI * r
  const offset = circumference - (score / 100) * circumference

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={r}
          fill="none"
          stroke={score >= 80 ? '#16a34a' : score >= 50 ? '#ca8a04' : '#dc2626'}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 48 48)"
        />
        <text x="48" y="48" textAnchor="middle" dominantBaseline="middle" className="text-2xl font-bold fill-current" style={{ fontSize: 22, fontWeight: 700 }}>
          {score}
        </text>
      </svg>
      <span className={`text-sm font-medium ${scoreColor(score)}`}>{scoreLabel(score)}</span>
    </div>
  )
}

// ─── Audit Detail Modal ───────────────────────────────────────────────────────

function AuditDetailModal({ audit, onClose }: { audit: SeoAudit; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={`SEO-Analyse: ${audit.url}`} size="lg">
      <div className="flex flex-col gap-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-500 truncate">{audit.url}</p>
            {audit.title && <p className="mt-1 font-medium">{audit.title}</p>}
            <p className="text-xs text-gray-400 mt-1">
              Analysiert: {audit.fetched_at ? formatDate(audit.fetched_at) : formatDate(audit.created_at)}
            </p>
          </div>
          <ScoreRing score={audit.score} />
        </div>

        {audit.error_message && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {audit.error_message}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Wörter', value: audit.word_count },
            { label: 'H1-Tags', value: audit.h1_count },
            { label: 'H2-Tags', value: audit.h2_count },
            { label: 'Int. Links', value: audit.internal_link_count },
          ].map((stat) => (
            <div key={stat.label} className="rounded-md border p-3 text-center">
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Checks */}
        {audit.checks.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2">Prüfungsergebnisse</h3>
            <div className="flex flex-col gap-2">
              {audit.checks.map((check) => (
                <div key={check.key} className="flex items-start gap-2 text-sm">
                  <span className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${checkStatusColor(check.status)}`}>
                    {checkStatusText(check.status)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{check.label}:</span>{' '}
                    <span className="text-gray-600">{check.message}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Keywords */}
        {audit.keywords.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2">Top-Keywords (Häufigkeit)</h3>
            <div className="flex flex-wrap gap-2">
              {audit.keywords.map((kw) => (
                <span key={kw} className="inline-block rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium text-gray-700">
                  {kw}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* AI Recommendations */}
        {audit.ai_recommendations && (
          <div>
            <h3 className="text-sm font-semibold mb-2">KI-Empfehlungen</h3>
            <div className="rounded-md bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900 whitespace-pre-wrap">
              {audit.ai_recommendations}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Analysis Tab ─────────────────────────────────────────────────────────────

function AnalysisTab() {
  const { toast } = useToast()
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [audits, setAudits] = useState<SeoAudit[]>([])
  const [auditsLoading, setAuditsLoading] = useState(true)
  const [auditsError, setAuditsError] = useState<string | null>(null)
  const [selectedAudit, setSelectedAudit] = useState<SeoAudit | null>(null)

  const loadAudits = useCallback(async () => {
    setAuditsLoading(true)
    setAuditsError(null)
    try {
      const resp = await apiClient.get<{ data: SeoAudit[] }>('/marketing/seo/audits?limit=50')
      setAudits(resp.data.data)
    } catch (err) {
      setAuditsError((err as Error).message)
    } finally {
      setAuditsLoading(false)
    }
  }, [])

  useEffect(() => { void loadAudits() }, [loadAudits])

  const handleAnalyze = async () => {
    if (!url.trim()) return
    setLoading(true)
    try {
      const resp = await apiClient.post<{ data: SeoAudit }>('/marketing/seo/analyze', { url: url.trim() })
      toast({ title: 'Analyse abgeschlossen', description: `Score: ${resp.data.data.score}/100` })
      setSelectedAudit(resp.data.data)
      void loadAudits()
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, tone: 'danger' })
    } finally {
      setLoading(false)
    }
  }

  const columns: ColumnDef<SeoAudit>[] = [
    {
      accessorKey: 'url',
      header: 'URL',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 min-w-0">
          <span className="truncate max-w-xs text-sm">{row.original.url}</span>
          <a href={row.original.url} target="_blank" rel="noreferrer" className="shrink-0 text-gray-400 hover:text-gray-600">
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ),
    },
    {
      accessorKey: 'score',
      header: 'Score',
      cell: ({ row }) => (
        <span className={`font-bold ${scoreColor(row.original.score)}`}>
          {row.original.score}/100
        </span>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Titel',
      cell: ({ row }) => (
        <span className="truncate max-w-[200px] block text-sm">{row.original.title ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'word_count',
      header: 'Wörter',
    },
    {
      accessorKey: 'created_at',
      header: 'Analysiert',
      cell: ({ row }) => <span className="text-xs text-gray-500">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => setSelectedAudit(row.original)}>
          Details
        </Button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      {/* URL Analyse */}
      <Card>
        <Card.Header>
          <h2 className="text-base font-semibold">Seite analysieren</h2>
          <p className="text-sm text-gray-500 mt-1">KI prüft Titel, Meta-Description, H1/H2-Struktur, Keywords und interne Verlinkung</p>
        </Card.Header>
        <Card.Body>
          <div className="flex gap-2">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/seite"
              onKeyDown={(e) => { if (e.key === 'Enter') void handleAnalyze() }}
              className="flex-1"
            />
            <Button
              variant="primary"
              onClick={() => void handleAnalyze()}
              disabled={loading || !url.trim()}
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              {loading ? 'Analysiere…' : 'Analysieren'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* History */}
      <Card>
        <Card.Header>
          <h2 className="text-base font-semibold">Analyse-Historie</h2>
        </Card.Header>
        <Card.Body className="p-0">
          {auditsLoading ? (
            <LoadingState />
          ) : auditsError ? (
            <ErrorState title="Fehler beim Laden" description={auditsError} />
          ) : audits.length === 0 ? (
            <EmptyState
              title="Noch keine Analysen"
              description="Geben Sie oben eine URL ein, um die erste SEO-Analyse zu starten."
            />
          ) : (
            <DataTable columns={columns} data={audits} />
          )}
        </Card.Body>
      </Card>

      {selectedAudit && (
        <AuditDetailModal audit={selectedAudit} onClose={() => setSelectedAudit(null)} />
      )}
    </div>
  )
}

// ─── URL Config Tab ───────────────────────────────────────────────────────────

function UrlConfigTab() {
  const { toast } = useToast()
  const [urls, setUrls] = useState<UrlConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newUrl, setNewUrl] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [adding, setAdding] = useState(false)

  const loadUrls = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiClient.get<{ data: UrlConfig[] }>('/marketing/seo/url-config')
      setUrls(resp.data.data)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void loadUrls() }, [loadUrls])

  const handleAdd = async () => {
    if (!newUrl.trim()) return
    setAdding(true)
    try {
      await apiClient.post('/marketing/seo/url-config', { url: newUrl.trim(), label: newLabel.trim() || undefined })
      setNewUrl('')
      setNewLabel('')
      toast({ title: 'URL hinzugefügt' })
      void loadUrls()
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, tone: 'danger' })
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/marketing/seo/url-config/${id}`)
      toast({ title: 'URL entfernt' })
      void loadUrls()
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, tone: 'danger' })
    }
  }

  const columns: ColumnDef<UrlConfig>[] = [
    {
      accessorKey: 'url',
      header: 'URL',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <span className="text-sm">{row.original.url}</span>
          <a href={row.original.url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600">
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ),
    },
    {
      accessorKey: 'label',
      header: 'Bezeichnung',
      cell: ({ row }) => <span className="text-sm text-gray-600">{row.original.label ?? '—'}</span>,
    },
    {
      accessorKey: 'created_at',
      header: 'Hinzugefügt',
      cell: ({ row }) => <span className="text-xs text-gray-500">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void handleDelete(row.original.id)}
          aria-label="URL entfernen"
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <Card.Header>
          <h2 className="text-base font-semibold">URL hinzufügen</h2>
          <p className="text-sm text-gray-500 mt-1">Seiten die täglich automatisch geprüft werden (Content-Audit Scheduled Job)</p>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <Input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://example.com/seite"
                className="flex-1"
              />
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Bezeichnung (optional)"
                className="w-52"
              />
              <Button variant="primary" onClick={() => void handleAdd()} disabled={adding || !newUrl.trim()}>
                <Plus className="h-4 w-4 mr-1" />
                Hinzufügen
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h2 className="text-base font-semibold">Überwachte URLs</h2>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState title="Fehler beim Laden" description={error} />
          ) : urls.length === 0 ? (
            <EmptyState
              title="Keine URLs konfiguriert"
              description="Fügen Sie oben URLs hinzu, die täglich analysiert werden sollen."
            />
          ) : (
            <DataTable columns={columns} data={urls} />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Keyword Monitoring Tab ───────────────────────────────────────────────────

function KeywordMonitoringTab() {
  const { toast } = useToast()
  const [gscAvailable, setGscAvailable] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<GscRow[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiClient.get<{ available: boolean }>('/marketing/gsc/status')
      .then((r) => setGscAvailable(r.data.available))
      .catch(() => setGscAvailable(false))
  }, [])

  const loadKeywords = async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiClient.post<{ data: { rows: GscRow[] } }>('/marketing/gsc/search-analytics', {
        dimensions: ['query'],
        rowLimit: 50,
      })
      setRows(resp.data.data.rows ?? [])
    } catch (err) {
      setError((err as Error).message)
      toast({ title: 'GSC-Fehler', description: (err as Error).message, tone: 'danger' })
    } finally {
      setLoading(false)
    }
  }

  const columns: ColumnDef<GscRow>[] = [
    {
      id: 'keyword',
      header: 'Keyword',
      cell: ({ row }) => <span className="font-medium">{row.original.keys[0]}</span>,
    },
    { accessorKey: 'clicks', header: 'Klicks' },
    { accessorKey: 'impressions', header: 'Impressionen' },
    {
      accessorKey: 'ctr',
      header: 'CTR',
      cell: ({ row }) => `${(row.original.ctr * 100).toFixed(1)} %`,
    },
    {
      accessorKey: 'position',
      header: 'Ø Position',
      cell: ({ row }) => row.original.position.toFixed(1),
    },
  ]

  if (gscAvailable === false) {
    return (
      <EmptyState
        title="Google Search Console nicht konfiguriert"
        description="Bitte Google Search Console unter Einstellungen → Adapter konfigurieren, um Keyword-Daten zu sehen."
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <Card.Header className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Keyword-Monitoring (Google Search Console)</h2>
            <p className="text-sm text-gray-500 mt-1">Top-Keywords der letzten 90 Tage</p>
          </div>
          <Button variant="secondary" onClick={() => void loadKeywords()} disabled={loading}>
            {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Laden
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState title="GSC-Fehler" description={error} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Keine Keyword-Daten"
              description='Klicken Sie „Laden", um die Top-Keywords der letzten 90 Tage zu laden.'
            />
          ) : (
            <DataTable columns={columns} data={rows} />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SeoPage() {
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="SEO-Analyse"
        breadcrumbs={[{ label: 'Marketing' }, { label: 'SEO-Analyse' }]}
      />

      <Tabs defaultValue="analysis">
        <TabsList>
          <TabsTrigger value="analysis">On-Page-Analyse</TabsTrigger>
          <TabsTrigger value="keywords">Keyword-Monitoring</TabsTrigger>
          <TabsTrigger value="urls">Überwachte URLs</TabsTrigger>
        </TabsList>

        <TabsContent value="analysis">
          <AnalysisTab />
        </TabsContent>

        <TabsContent value="keywords">
          <KeywordMonitoringTab />
        </TabsContent>

        <TabsContent value="urls">
          <UrlConfigTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
